/**
 * @fileoverview Attribution Handler
 * 
 * Captures UTM parameters and referrer data on first visit.
 * Persists attribution to database when user signs up.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const ATTRIBUTION_KEY = 'pending_attribution';

interface AttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer_url: string | null;
  landing_page: string;
}

// Capture attribution data from URL on first visit
function captureAttribution(): AttributionData | null {
  const searchParams = new URLSearchParams(window.location.search);
  
  const attribution: AttributionData = {
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
    referrer_url: document.referrer || null,
    landing_page: window.location.pathname,
  };

  // Only store if we have any meaningful attribution data
  const hasUtm = attribution.utm_source || attribution.utm_medium || attribution.utm_campaign;
  const hasReferrer = attribution.referrer_url && !attribution.referrer_url.includes(window.location.host);
  
  if (hasUtm || hasReferrer) {
    return attribution;
  }
  
  return null;
}

export function AttributionHandler() {
  const { user } = useAuth();
  const location = useLocation();
  const hasProcessedRef = useRef(false);
  const hasCapturedRef = useRef(false);

  // Capture attribution on first page load
  useEffect(() => {
    if (hasCapturedRef.current) return;
    
    // Don't overwrite existing attribution
    const existing = localStorage.getItem(ATTRIBUTION_KEY);
    if (existing) {
      hasCapturedRef.current = true;
      return;
    }

    const attribution = captureAttribution();
    if (attribution) {
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
      console.debug('Attribution captured:', attribution);
    }
    
    hasCapturedRef.current = true;
  }, []);

  // Process attribution when user signs up
  useEffect(() => {
    if (!user || hasProcessedRef.current) return;

    const processAttribution = async () => {
      const pendingData = localStorage.getItem(ATTRIBUTION_KEY);
      if (!pendingData) return;

      try {
        const attribution: AttributionData = JSON.parse(pendingData);
        
        // Insert attribution data (uses type assertion since table is new)
        const { error } = await (supabase
          .from('user_attribution' as any)
          .upsert({
            user_id: user.id,
            ...attribution,
          }, {
            onConflict: 'user_id',
            ignoreDuplicates: true,
          }) as any);

        if (error) {
          // Duplicate key is expected for existing users
          if (!error.message?.includes('duplicate')) {
            console.debug('Attribution save failed:', error);
          }
        } else {
          console.debug('Attribution saved for user:', user.id);
        }

        // Clear pending data after processing
        localStorage.removeItem(ATTRIBUTION_KEY);
        hasProcessedRef.current = true;
      } catch (e) {
        console.debug('Attribution processing error:', e);
      }
    };

    processAttribution();
  }, [user]);

  return null;
}
